import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, UserPlus, Save, Trash2, Image as ImageIcon, Upload, Loader2 } from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function StoreDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [store, setStore] = useState(null);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(null);
  const [openInvite, setOpenInvite] = useState(false);
  const [invite, setInvite] = useState({ name: "", email: "", password: "", phone: "" });
  const [resetting, setResetting] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [pendingLogin, setPendingLogin] = useState(null);

  const [logoBusy, setLogoBusy] = useState(false);
  const logoInputRef = useRef(null);
  const canEdit = user.role === "admin" || user.store_id === id;
  const canManageLogins = user.role === "admin";

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const logoSrc = store?.logo_path && token
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${store.logo_path}?auth=${encodeURIComponent(token)}`
    : "";

  const pickLogo = () => logoInputRef.current?.click();
  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setLogoBusy(true);
    try {
      const { data } = await api.post(`/stores/${id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStore(data); setForm(data);
      toast.success("Logo updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Logo upload failed");
    } finally { setLogoBusy(false); }
  };
  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      const { data } = await api.delete(`/stores/${id}/logo`);
      setStore(data); setForm(data);
      toast.success("Logo removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setLogoBusy(false); }
  };

  const reload = async () => {
    const stores = await api.get("/stores");
    const s = stores.data.find(x => x.id === id);
    setStore(s); setForm(s);
    if (canManageLogins) {
      try {
        const u = await api.get(`/stores/${id}/users`);
        setUsers(u.data);
      } catch (err) {
        // 403 for non-admin views is expected — otherwise surface it
        if (err.response?.status && err.response.status !== 403) {
          toast.error(err.response?.data?.detail || "Couldn't load logins");
        }
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [id]);

  const saveStore = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name, address: form.address, phone: form.phone,
        email: form.email, manager_name: form.manager_name,
        team_members: form.team_members || [],
      };
      // Only admins may change the store code; send it only if it actually changed
      if (user.role === "admin" && (form.code || "").trim() && form.code.trim().toUpperCase() !== (store.code || "")) {
        payload.code = form.code.trim().toUpperCase();
      }
      await api.put(`/stores/${id}`, payload);
      toast.success("Store updated");
      navigate("/stores");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/stores/${id}/users`, { ...invite, role: "bookstore_user" });
      toast.success("Login created"); setOpenInvite(false);
      setInvite({ name: "", email: "", password: "", phone: "" }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users/reset-password", { user_id: resetting.id, new_password: newPwd });
      toast.success("Password reset"); setResetting(null); setNewPwd("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteLogin = (u) => {
    if (u.role === "admin") return toast.error("Cannot delete an admin from this screen");
    setPendingLogin(u);
  };
  const doDeleteLogin = async () => {
    const u = pendingLogin;
    if (!u) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Login deleted");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setPendingLogin(null); }
  };

  if (!store || !form) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="store-detail-page">
      <div>
        <Link to="/stores" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3"/> Back to stores
        </Link>
        <div className="overline mt-3">{store.code}</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">{store.name}</h1>
      </div>

      <div className={`grid gap-6 ${canManageLogins ? "lg:grid-cols-5" : ""}`}>
        <div className={`space-y-6 ${canManageLogins ? "lg:col-span-3" : "max-w-2xl"}`}>
          {/* Store logo */}
          <div className="grid-border-card p-6">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-sm bg-secondary flex items-center justify-center overflow-hidden border border-border shrink-0" data-testid="store-logo-preview">
                {logoSrc ? (
                  <img src={logoSrc} alt={`${store.name} logo`} className="w-full h-full object-contain"/>
                ) : (
                  <ImageIcon className="w-8 h-8 text-muted-foreground" strokeWidth={1.25}/>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-display text-2xl">Store logo</h2>
                <p className="text-sm text-muted-foreground mt-1">Shown on this store's PDF reports and receipts.</p>
                {canEdit && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button type="button" variant="outline" size="sm" onClick={pickLogo} disabled={logoBusy} data-testid="store-logo-upload-btn">
                      {logoBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <Upload className="w-3.5 h-3.5 mr-1.5"/>}
                      {logoSrc ? "Change logo" : "Upload logo"}
                    </Button>
                    {logoSrc && (
                      <Button type="button" variant="ghost" size="sm" onClick={removeLogo} disabled={logoBusy} data-testid="store-logo-remove-btn">
                        Remove
                      </Button>
                    )}
                    <input type="file" accept="image/*" ref={logoInputRef} onChange={uploadLogo} className="hidden"/>
                  </div>
                )}
              </div>
            </div>
          </div>

          <form onSubmit={saveStore} className="grid-border-card p-6 space-y-4">
          <h2 className="font-display text-2xl">Contact information</h2>
          <fieldset disabled={!canEdit} className="space-y-4">
            {user.role === "admin" && (
              <div>
                <Label>Store code</Label>
                <Input
                  value={form.code || ""}
                  onChange={e => setForm({...form, code: e.target.value.toUpperCase()})}
                  placeholder="e.g. BKR-01"
                  className="font-mono uppercase"
                  data-testid="store-detail-code"
                />
                <div className="text-xs text-muted-foreground mt-1">Uppercase identifier used across audit logs and reports.</div>
              </div>
            )}
            <div><Label>Store name</Label><Input value={form.name||""} onChange={e=>setForm({...form, name:e.target.value})} data-testid="store-detail-name"/></div>
            <div><Label>Address</Label><Input value={form.address||""} onChange={e=>setForm({...form, address:e.target.value})} data-testid="store-detail-address"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={form.phone||""} onChange={e=>setForm({...form, phone:e.target.value})} data-testid="store-detail-phone"/></div>
              <div><Label>Email</Label><Input type="email" value={form.email||""} onChange={e=>setForm({...form, email:e.target.value})} data-testid="store-detail-email"/></div>
            </div>
            <div><Label>Manager name</Label><Input value={form.manager_name||""} onChange={e=>setForm({...form, manager_name:e.target.value})} data-testid="store-detail-manager"/></div>

            <div className="border border-border rounded-sm p-4 space-y-3" data-testid="store-team-members">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Inventory team</Label>
                <p className="text-xs text-muted-foreground mt-1">People responsible for keeping this store's inventory up to date.</p>
              </div>
              {canEdit && (
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a team member name…"
                    value={form.team_input || ""}
                    onChange={e=>setForm({...form, team_input:e.target.value})}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const n = (form.team_input || "").trim();
                        if (!n) return;
                        const list = form.team_members || [];
                        if (list.includes(n)) return;
                        setForm({ ...form, team_members: [...list, n], team_input: "" });
                      }
                    }}
                    data-testid="store-team-input"
                  />
                  <Button type="button" variant="outline" onClick={() => {
                    const n = (form.team_input || "").trim();
                    if (!n) return;
                    const list = form.team_members || [];
                    if (list.includes(n)) return;
                    setForm({ ...form, team_members: [...list, n], team_input: "" });
                  }} data-testid="store-team-add">Add</Button>
                </div>
              )}
              {(form.team_members || []).length > 0 ? (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="store-team-chips">
                  {(form.team_members || []).map(n => (
                    <span key={n} className="inline-flex items-center gap-1.5 text-xs bg-secondary text-secondary-foreground px-2.5 py-1 rounded-full">
                      {n}
                      {canEdit && (
                        <button type="button" onClick={() => setForm({ ...form, team_members: form.team_members.filter(x => x !== n) })} className="text-muted-foreground hover:text-destructive" aria-label={`Remove ${n}`}>×</button>
                      )}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground italic">No team members yet.</div>
              )}
            </div>
          </fieldset>
          {canEdit && <Button type="submit" data-testid="store-detail-save"><Save className="w-4 h-4 mr-2"/>Save changes</Button>}
        </form>
        </div>

        {canManageLogins && (
          <div className="grid-border-card p-6 lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl">Logins</h2>
              <Dialog open={openInvite} onOpenChange={setOpenInvite}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="invite-user-btn"><UserPlus className="w-3.5 h-3.5 mr-1.5"/>Create login</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-display">Create login for this store</DialogTitle>
                    <DialogDescription className="sr-only">Create a bookstore user account attached to this store.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={submitInvite} className="space-y-3">
                    <div><Label>Name</Label><Input value={invite.name} onChange={e=>setInvite({...invite, name:e.target.value})} required data-testid="invite-name"/></div>
                    <div><Label>Email</Label><Input type="email" value={invite.email} onChange={e=>setInvite({...invite, email:e.target.value})} required data-testid="invite-email"/></div>
                    <div><Label>Temporary password</Label><Input type="password" value={invite.password} onChange={e=>setInvite({...invite, password:e.target.value})} required minLength={6} data-testid="invite-password"/></div>
                    <div><Label>Phone (optional)</Label><Input value={invite.phone} onChange={e=>setInvite({...invite, phone:e.target.value})}/></div>
                    <DialogFooter><Button type="submit" data-testid="invite-submit">Create login</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="divide-y divide-border -mx-2">
              {users.map(u => (
                <div key={u.id} className="flex items-center justify-between gap-3 px-2 py-3" data-testid={`store-login-row-${u.email}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{u.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                  </div>
                  {u.role !== "admin" && (
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={()=>setResetting(u)} data-testid={`reset-pwd-${u.email}`}>
                        <KeyRound className="w-3.5 h-3.5 mr-1.5"/>Reset
                      </Button>
                      <Button variant="ghost" size="icon" onClick={()=>deleteLogin(u)} data-testid={`delete-login-${u.email}`} title="Delete login">
                        <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive"/>
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {users.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No logins yet</div>}
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!resetting} onOpenChange={(o)=>{ if(!o){ setResetting(null); setNewPwd(""); }}}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Reset password</DialogTitle>
            <DialogDescription className="sr-only">Set a new password for this user.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReset} className="space-y-3">
            <div className="text-sm text-muted-foreground">For {resetting?.email}</div>
            <div><Label>New password</Label><Input type="password" value={newPwd} onChange={e=>setNewPwd(e.target.value)} required minLength={6} data-testid="reset-new-password"/></div>
            <DialogFooter><Button type="submit" data-testid="reset-submit">Set password</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingLogin}
        onOpenChange={(o)=>{ if(!o) setPendingLogin(null); }}
        title={`Delete login "${pendingLogin?.email}"?`}
        description="This permanently removes the login. History for this store is untouched."
        confirmLabel="Delete login"
        destructive
        onConfirm={doDeleteLogin}
        testid="confirm-delete-login"
      />
    </div>
  );
}
